"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Plus, Trash2 } from "lucide-react";
import { SlideUpPage } from "@/components/layout/SlideUpPage";
import type { Profile, WorkExperience, Education } from "@/types";

interface EditProfileFormProps {
  profile?: Profile | null;
  onClose: () => void;
  onSuccess: () => void;
}

const REMOTE_PREFERENCES = [
  { value: "remote", label: "Fjernarbeid" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "På kontoret" },
  { value: "flexible", label: "Fleksibelt" },
];

const JOB_TYPES = [
  { value: "full-time", label: "Heltid" },
  { value: "part-time", label: "Deltid" },
  { value: "contract", label: "Kontrakt" },
  { value: "internship", label: "Internship" },
];

export function EditProfileForm({ profile, onClose, onSuccess }: EditProfileFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"basic" | "experience" | "education" | "preferences">(
    "basic"
  );

  const [formData, setFormData] = useState({
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    bio: profile?.bio || "",
    location: profile?.location || "",
    topSkills: profile?.topSkills || [],
    workExperience: profile?.workExperience || [] as WorkExperience[],
    education: profile?.education || [] as Education[],
    preferredLocations: profile?.preferredLocations || [],
    jobTypes: profile?.jobTypes || ["full-time"],
    remotePreference: profile?.remotePreference || "flexible" as const,
    salaryMin: profile?.salaryExpectation?.min?.toString() || "",
    salaryMax: profile?.salaryExpectation?.max?.toString() || "",
    showSalary: !!profile?.salaryExpectation,
  });

  const addSkill = (skill: string) => {
    if (skill && !formData.topSkills.includes(skill) && formData.topSkills.length < 10) {
      setFormData({ ...formData, topSkills: [...formData.topSkills, skill] });
    }
  };

  const removeSkill = (skill: string) => {
    setFormData({ ...formData, topSkills: formData.topSkills.filter((s) => s !== skill) });
  };

  const addWorkExperience = () => {
    setFormData({
      ...formData,
      workExperience: [
        ...formData.workExperience,
        {
          company: "",
          title: "",
          startDate: "",
          endDate: "",
          current: false,
          description: "",
        },
      ],
    });
  };

  const removeWorkExperience = (index: number) => {
    setFormData({
      ...formData,
      workExperience: formData.workExperience.filter((_, i) => i !== index),
    });
  };

  const updateWorkExperience = (index: number, field: keyof WorkExperience, value: any) => {
    const newExperience = [...formData.workExperience];
    newExperience[index] = { ...newExperience[index], [field]: value };
    setFormData({ ...formData, workExperience: newExperience });
  };

  const addEducation = () => {
    setFormData({
      ...formData,
      education: [
        ...formData.education,
        {
          institution: "",
          degree: "",
          field: "",
          startDate: "",
          endDate: "",
          current: false,
        },
      ],
    });
  };

  const removeEducation = (index: number) => {
    setFormData({
      ...formData,
      education: formData.education.filter((_, i) => i !== index),
    });
  };

  const updateEducation = (index: number, field: keyof Education, value: any) => {
    const newEducation = [...formData.education];
    newEducation[index] = { ...newEducation[index], [field]: value };
    setFormData({ ...formData, education: newEducation });
  };

  const addPreferredLocation = (location: string) => {
    if (location && !formData.preferredLocations.includes(location)) {
      setFormData({
        ...formData,
        preferredLocations: [...formData.preferredLocations, location],
      });
    }
  };

  const removePreferredLocation = (location: string) => {
    setFormData({
      ...formData,
      preferredLocations: formData.preferredLocations.filter((l) => l !== location),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      // TODO: Replace with actual Convex mutation
      // const profileData = {
      //   firstName: formData.firstName,
      //   lastName: formData.lastName,
      //   bio: formData.bio,
      //   location: formData.location,
      //   topSkills: formData.topSkills,
      //   workExperience: formData.workExperience,
      //   education: formData.education,
      //   preferredLocations: formData.preferredLocations.length ? formData.preferredLocations : [formData.location],
      //   jobTypes: formData.jobTypes,
      //   remotePreference: formData.remotePreference,
      //   salaryExpectation: formData.showSalary && formData.salaryMin && formData.salaryMax
      //     ? {
      //         min: parseInt(formData.salaryMin),
      //         max: parseInt(formData.salaryMax),
      //         currency: "NOK",
      //       }
      //     : undefined,
      // };
      // await upsertProfile(profileData);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Profile updated:", formData);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Kunne ikke lagre profil");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SlideUpPage onClose={onClose} showCloseButton={true}>
      <div className="min-h-full" style={{ backgroundColor: "var(--color-bg-primary)" }}>
        {/* Header */}
        <div className="border-b p-6 pt-16" style={{ borderColor: "var(--color-border)" }}>
          <h2
            style={{
              fontSize: "var(--font-size-title-md)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--color-text-primary)",
            }}
          >
            Rediger profil
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: "var(--color-border)" }}>
          {[
            { id: "basic", label: "Grunninfo" },
            { id: "experience", label: "Erfaring" },
            { id: "education", label: "Utdanning" },
            { id: "preferences", label: "Preferanser" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className="flex-1 px-4 py-3 text-sm font-medium transition-colors"
              style={{
                color: activeTab === tab.id ? "var(--color-text-primary)" : "var(--color-text-secondary)",
                borderBottom: activeTab === tab.id ? "2px solid var(--color-bg-accent)" : "none",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="p-6">
            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="firstName" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                      Fornavn *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full rounded-lg border px-4 py-2.5"
                      style={{
                        backgroundColor: "var(--color-bg-secondary)",
                        borderColor: "var(--color-border)",
                        color: "var(--color-text-primary)",
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor="lastName" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                      Etternavn *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full rounded-lg border px-4 py-2.5"
                      style={{
                        backgroundColor: "var(--color-bg-secondary)",
                        borderColor: "var(--color-border)",
                        color: "var(--color-text-primary)",
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="location" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    Lokasjon *
                  </label>
                  <input
                    type="text"
                    id="location"
                    required
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full rounded-lg border px-4 py-2.5"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      borderColor: "var(--color-border)",
                      color: "var(--color-text-primary)",
                    }}
                    placeholder="Oslo, Bergen, etc."
                  />
                </div>

                <div>
                  <label htmlFor="bio" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    Om meg *
                  </label>
                  <textarea
                    id="bio"
                    required
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={4}
                    className="w-full rounded-lg border px-4 py-2.5"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      borderColor: "var(--color-border)",
                      color: "var(--color-text-primary)",
                    }}
                    placeholder="Fortell om deg selv..."
                  />
                </div>

                <div>
                  <label htmlFor="newSkill" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    Ferdigheter (maks 10)
                  </label>
                  <input
                    type="text"
                    id="newSkill"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addSkill((e.target as HTMLInputElement).value);
                        (e.target as HTMLInputElement).value = "";
                      }
                    }}
                    className="w-full rounded-lg border px-4 py-2.5"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      borderColor: "var(--color-border)",
                      color: "var(--color-text-primary)",
                    }}
                    placeholder="Skriv ferdighet og trykk Enter"
                  />
                  {formData.topSkills.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.topSkills.map((skill) => (
                        <span
                          key={skill}
                          className="flex items-center gap-1 rounded-full px-3 py-1 text-sm"
                          style={{
                            backgroundColor: "var(--color-bg-accent)",
                            color: "var(--color-text-primary)",
                          }}
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() => removeSkill(skill)}
                            className="ml-1 hover:opacity-70"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Work Experience Tab */}
            {activeTab === "experience" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 style={{ fontSize: "var(--font-size-body-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>
                    Arbeidserfaring
                  </h3>
                  <button
                    type="button"
                    onClick={addWorkExperience}
                    className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Legg til
                  </button>
                </div>

                {formData.workExperience.length === 0 ? (
                  <p className="text-center py-8 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    Ingen arbeidserfaring lagt til ennå
                  </p>
                ) : (
                  formData.workExperience.map((exp, index) => (
                    <div
                      key={index}
                      className="rounded-lg border p-4 space-y-3"
                      style={{
                        backgroundColor: "var(--color-bg-secondary)",
                        borderColor: "var(--color-border)",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                          Erfaring {index + 1}
                        </h4>
                        <button
                          type="button"
                          onClick={() => removeWorkExperience(index)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Firma *
                          </label>
                          <input
                            type="text"
                            required
                            value={exp.company}
                            onChange={(e) => updateWorkExperience(index, "company", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Tittel *
                          </label>
                          <input
                            type="text"
                            required
                            value={exp.title}
                            onChange={(e) => updateWorkExperience(index, "title", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Startdato *
                          </label>
                          <input
                            type="month"
                            required
                            value={exp.startDate}
                            onChange={(e) => updateWorkExperience(index, "startDate", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Sluttdato
                          </label>
                          <input
                            type="month"
                            value={exp.endDate || ""}
                            disabled={exp.current}
                            onChange={(e) => updateWorkExperience(index, "endDate", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`current-${index}`}
                          checked={exp.current}
                          onChange={(e) => updateWorkExperience(index, "current", e.target.checked)}
                          className="h-4 w-4 rounded"
                        />
                        <label htmlFor={`current-${index}`} className="text-sm" style={{ color: "var(--color-text-primary)" }}>
                          Nåværende stilling
                        </label>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                          Beskrivelse *
                        </label>
                        <textarea
                          required
                          value={exp.description}
                          onChange={(e) => updateWorkExperience(index, "description", e.target.value)}
                          rows={3}
                          className="w-full rounded border px-3 py-2 text-sm"
                          style={{
                            backgroundColor: "var(--color-bg-primary)",
                            borderColor: "var(--color-border)",
                            color: "var(--color-text-primary)",
                          }}
                          placeholder="Beskriv dine oppgaver og ansvar..."
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Education Tab */}
            {activeTab === "education" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 style={{ fontSize: "var(--font-size-body-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}>
                    Utdanning
                  </h3>
                  <button
                    type="button"
                    onClick={addEducation}
                    className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Legg til
                  </button>
                </div>

                {formData.education.length === 0 ? (
                  <p className="text-center py-8 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    Ingen utdanning lagt til ennå
                  </p>
                ) : (
                  formData.education.map((edu, index) => (
                    <div
                      key={index}
                      className="rounded-lg border p-4 space-y-3"
                      style={{
                        backgroundColor: "var(--color-bg-secondary)",
                        borderColor: "var(--color-border)",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                          Utdanning {index + 1}
                        </h4>
                        <button
                          type="button"
                          onClick={() => removeEducation(index)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Institusjon *
                          </label>
                          <input
                            type="text"
                            required
                            value={edu.institution}
                            onChange={(e) => updateEducation(index, "institution", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Grad/utdanning *
                          </label>
                          <input
                            type="text"
                            required
                            value={edu.degree}
                            onChange={(e) => updateEducation(index, "degree", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Fagfelt *
                          </label>
                          <input
                            type="text"
                            required
                            value={edu.field}
                            onChange={(e) => updateEducation(index, "field", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Startdato *
                          </label>
                          <input
                            type="month"
                            required
                            value={edu.startDate}
                            onChange={(e) => updateEducation(index, "startDate", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                            Sluttdato
                          </label>
                          <input
                            type="month"
                            value={edu.endDate || ""}
                            disabled={edu.current}
                            onChange={(e) => updateEducation(index, "endDate", e.target.value)}
                            className="w-full rounded border px-3 py-2 text-sm"
                            style={{
                              backgroundColor: "var(--color-bg-primary)",
                              borderColor: "var(--color-border)",
                              color: "var(--color-text-primary)",
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`edu-current-${index}`}
                          checked={edu.current}
                          onChange={(e) => updateEducation(index, "current", e.target.checked)}
                          className="h-4 w-4 rounded"
                        />
                        <label htmlFor={`edu-current-${index}`} className="text-sm" style={{ color: "var(--color-text-primary)" }}>
                          Pågående
                        </label>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === "preferences" && (
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    Foretrukne lokasjoner
                  </label>
                  <input
                    type="text"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addPreferredLocation((e.target as HTMLInputElement).value);
                        (e.target as HTMLInputElement).value = "";
                      }
                    }}
                    className="w-full rounded-lg border px-4 py-2.5"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      borderColor: "var(--color-border)",
                      color: "var(--color-text-primary)",
                    }}
                    placeholder="Skriv lokasjon og trykk Enter"
                  />
                  {formData.preferredLocations.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.preferredLocations.map((loc) => (
                        <span
                          key={loc}
                          className="flex items-center gap-1 rounded-full px-3 py-1 text-sm"
                          style={{
                            backgroundColor: "var(--color-bg-accent)",
                            color: "var(--color-text-primary)",
                          }}
                        >
                          {loc}
                          <button
                            type="button"
                            onClick={() => removePreferredLocation(loc)}
                            className="ml-1 hover:opacity-70"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    Jobbtyper
                  </label>
                  <div className="space-y-2">
                    {JOB_TYPES.map((type) => (
                      <div key={type.value} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={type.value}
                          checked={formData.jobTypes.includes(type.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, jobTypes: [...formData.jobTypes, type.value] });
                            } else {
                              setFormData({ ...formData, jobTypes: formData.jobTypes.filter((t) => t !== type.value) });
                            }
                          }}
                          className="h-4 w-4 rounded"
                        />
                        <label htmlFor={type.value} className="text-sm" style={{ color: "var(--color-text-primary)" }}>
                          {type.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="remotePreference" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    Arbeidsmåte
                  </label>
                  <select
                    id="remotePreference"
                    value={formData.remotePreference}
                    onChange={(e) => setFormData({ ...formData, remotePreference: e.target.value as any })}
                    className="w-full rounded-lg border px-4 py-2.5"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      borderColor: "var(--color-border)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    {REMOTE_PREFERENCES.map((pref) => (
                      <option key={pref.value} value={pref.value}>
                        {pref.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3 rounded-lg border p-4" style={{ borderColor: "var(--color-border)" }}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="showSalary"
                      checked={formData.showSalary}
                      onChange={(e) => setFormData({ ...formData, showSalary: e.target.checked })}
                      className="h-4 w-4 rounded"
                    />
                    <label
                      htmlFor="showSalary"
                      className="text-sm font-medium"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      Lønnsforventning
                    </label>
                  </div>

                  {formData.showSalary && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                          Fra (NOK)
                        </label>
                        <input
                          type="number"
                          value={formData.salaryMin}
                          onChange={(e) => setFormData({ ...formData, salaryMin: e.target.value })}
                          className="w-full rounded border px-3 py-2 text-sm"
                          style={{
                            backgroundColor: "var(--color-bg-secondary)",
                            borderColor: "var(--color-border)",
                            color: "var(--color-text-primary)",
                          }}
                          placeholder="600000"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs" style={{ color: "var(--color-text-secondary)" }}>
                          Til (NOK)
                        </label>
                        <input
                          type="number"
                          value={formData.salaryMax}
                          onChange={(e) => setFormData({ ...formData, salaryMax: e.target.value })}
                          className="w-full rounded border px-3 py-2 text-sm"
                          style={{
                            backgroundColor: "var(--color-bg-secondary)",
                            borderColor: "var(--color-border)",
                            color: "var(--color-text-primary)",
                          }}
                          placeholder="800000"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div
              className="mx-6 mb-4 rounded-lg p-3 text-sm"
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
              }}
            >
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 border-t p-6" style={{ borderColor: "var(--color-border)" }}>
            <Button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 rounded-lg py-3 font-semibold"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                color: "var(--color-text-primary)",
              }}
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 rounded-lg py-3 font-semibold"
              style={{
                backgroundColor: "var(--color-bg-accent)",
                color: "var(--color-text-primary)",
              }}
            >
              {isSubmitting ? "Lagrer..." : "Lagre profil"}
            </Button>
          </div>
        </form>
      </div>
    </SlideUpPage>
  );
}

